import { useEffect, useMemo, useState } from "react";
import {
  addDocumentsToFleetOrder,
  createFleetOrder,
  getCoDrivers,
  getDrivers,
  getDepots,
  getFleetOrder,
  getFleetOrders,
  getFoRoutes,
  getNextFleetOrderNumber,
  getVehicles,
  prepareFleetDocuments,
} from "../../api/client.js";
import { Dialog } from "../Dialog.jsx";
import { RouteBadge } from "../RouteBadge.jsx";
import {
  PlanningStatusBadge,
  ProcessingStatusBadge,
} from "../StatusBadge.jsx";
import {
  formatVolume,
  formatWeight,
} from "../../utils/number-format.js";

function todayInJakarta() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jakarta",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function formatDate(value) {
  if (!value) return "—";

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "—";

  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function isOperationalDepot(depot) {
  return (
    depot?.active === true &&
    String(depot?.coordinateStatus || "").toUpperCase() === "VALID" &&
    Number.isFinite(Number(depot?.latitude)) &&
    Number.isFinite(Number(depot?.longitude))
  );
}

function depotOptionLabel(depot) {
  const identity = [depot?.depotName, depot?.depotCode]
    .filter(Boolean)
    .join(" · ");

  if (!depot?.active) {
    return `${identity || "Depot"} · Tidak aktif`;
  }

  if (isOperationalDepot(depot)) {
    return depot?.isDefault ? `${identity} · Default` : identity;
  }

  return `${identity || "Depot"} · Koordinat belum valid`;
}

function ReadOnlyFleetOrder({ fleetOrder }) {
  if (!fleetOrder) {
    return (
      <div className="fleet-order-placeholder">
        Pilih Fleet Order untuk melihat detail.
      </div>
    );
  }

  const fields = [
    ["Nomor FO", fleetOrder.foNumber],
    ["Tanggal FO", formatDate(fleetOrder.foDate)],
    ["Tanggal Berangkat", formatDate(fleetOrder.departureDate)],
    ["Estimasi Pulang", formatDate(fleetOrder.estimatedReturnDate)],
    [
      "Depot Asal",
      fleetOrder.depot?.depotName ||
        fleetOrder.depotName ||
        "Belum dipilih",
    ],
    ["Nama Driver", fleetOrder.driverName],
    ["Nama Co-Driver", fleetOrder.coDriverName || "—"],
    ["Nopol Kendaraan", fleetOrder.vehiclePlate],
    ["Rute FO", fleetOrder.foRouteName || fleetOrder.routeName || "—"],
  ];

  return (
    <dl className="fleet-order-readonly">
      {fields.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function normalizeStatusClass(value, fallback = "unknown") {
  return String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");
}

function LogisticsStatus({ order }) {
  const normalizedStatus = String(
    order.calculationStatus || "NOT_CALCULATED",
  ).toUpperCase();

  const label =
    normalizedStatus === "COMPLETE"
      ? "Lengkap"
      : normalizedStatus === "WARNING"
        ? "Perlu diperiksa"
        : normalizedStatus === "BLOCKED"
          ? "Belum lengkap"
          : "Belum dihitung";

  return (
    <span
      className={`logistics-status logistics-status--${normalizeStatusClass(
        normalizedStatus,
        "not_calculated",
      )}`}
    >
      {label}
      {order.warningCount ? ` · ${order.warningCount}` : ""}
    </span>
  );
}

function SelectedOrdersTable({ orders, selectedKeys, onToggle }) {
  const showSelection = orders.some((order) => order.documentType === "DELIVERY_ORDER");
  return (
    <div className="fleet-order-selected">
      <div className="fleet-order-selected__heading">
        <h3>Dokumen Dipilih</h3>
        <span>{orders.length} dokumen</span>
      </div>

      <div className="fleet-order-table-wrap">
        <table className="fleet-order-table">
          <thead>
            <tr>
              {showSelection && <th>Pilih</th>}
              <th>Nomor Pesanan</th>
              {showSelection && <th>Sales Order Asal</th>}
              <th>Tanggal</th>
              <th>Pelanggan</th>
              <th>Area</th>
              <th>Rute</th>
              <th>Status Proses</th>
              <th>Status Planning</th>
              <th>Berat</th>
              <th>Volume</th>
              <th>Status Detail</th>
            </tr>
          </thead>

          <tbody>
            {orders.map((order) => (
              <tr
                key={
                  order.documentKey ||
                  `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`
                }
              >
                {showSelection && (
                  <td>
                    <input
                      aria-label={`Pilih ${order.documentNumber || order.soNumber}`}
                      checked={selectedKeys.has(order.documentKey)}
                      onChange={() => onToggle(order.documentKey)}
                      type="checkbox"
                    />
                  </td>
                )}
                <td>
                  <strong>{order.documentNumber || order.soNumber}</strong>
                </td>
                {showSelection && <td>{order.salesOrderNumber || "—"}</td>}

                <td>{formatDate(order.documentDate || order.orderDate)}</td>

                <td>{order.customerName}</td>

                <td>{order.resolvedArea || "Belum tersedia"}</td>

                <td>
                  <RouteBadge
                    color={order.resolvedRouteColor}
                    name={
                      order.resolvedRouteName ||
                      order.resolvedRouteCode ||
                      order.resolvedRoute
                    }
                  />
                </td>

                <td>
                  {order.processingStatus ? (
                    <ProcessingStatusBadge
                      label={order.processingStatusLabel}
                      status={order.processingStatus}
                    />
                  ) : (
                    <span className="status-badge status-badge--processing-unknown">
                      {order.processingStatusLabel || "—"}
                    </span>
                  )}
                </td>

                <td>
                  {order.planningStatus ? (
                    <PlanningStatusBadge
                      label={order.planningStatusLabel}
                      status={order.planningStatus}
                    />
                  ) : (
                    <span className="status-badge status-badge--planning-unknown">
                      {order.planningStatusLabel || "—"}
                    </span>
                  )}
                </td>

                <td>
                  {formatWeight(order.totalWeightKg)}
                </td>

                <td>
                  {formatVolume(order.totalVolumeM3)}
                </td>

                <td>
                  <LogisticsStatus order={order} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FleetOrderModal({
  onCancel,
  onSuccess,
  selectedDocuments,
  selectedOrders = [],
}) {
  const documents = selectedDocuments || selectedOrders;

  const selectedRouteNames = new Set(
    documents.map((order) => order.resolvedRouteName).filter(Boolean),
  );

  const inferredRouteName =
    selectedRouteNames.size === 1 ? [...selectedRouteNames][0] : null;

  const [mode, setMode] = useState("new");
  const [drivers, setDrivers] = useState([]);
  const [depots, setDepots] = useState([]);
  const [coDrivers, setCoDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [foRoutes, setFoRoutes] = useState([]);
  const [fleetOrders, setFleetOrders] = useState([]);
  const [selectedFleetOrderId, setSelectedFleetOrderId] = useState("");
  const [selectedFleetOrder, setSelectedFleetOrder] = useState(null);
  const [foNumber, setFoNumber] = useState("Memuat…");

  const [form, setForm] = useState({
    foDate: todayInJakarta(),
    departureDate: "",
    estimatedReturnDate: "",
    depotId: "",
    driverId: "",
    coDriverId: "",
    vehicleId: "",
    foRouteId: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [prepared, setPrepared] = useState(null);
  const [selectedDocumentKeys, setSelectedDocumentKeys] = useState(
    () => new Set(documents.map((order) => order.documentKey || `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`)),
  );

  const documentRefs = useMemo(
    () =>
      documents.filter((order) => selectedDocumentKeys.has(
        order.documentKey || `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`,
      )).map((order) => ({
        documentType: order.documentType || "SALES_ORDER",
        documentId: order.documentId || order.id,
      })),
    [documents, selectedDocumentKeys],
  );

  useEffect(() => {
    Promise.all([
      getDrivers(),
      getDepots(),
      getCoDrivers(),
      getVehicles(),
      getFoRoutes(),
      getFleetOrders({ status: "SCHEDULED", limit: 300 }),
      getNextFleetOrderNumber(form.foDate),
      prepareFleetDocuments(documentRefs),
    ])
      .then(
        ([
          driverPayload,
          depotPayload,
          coDriverPayload,
          vehiclePayload,
          foRoutePayload,
          fleetPayload,
          numberPayload,
          preparedPayload,
        ]) => {
          setDrivers(driverPayload.drivers || []);

          const nextDepots = depotPayload.depots || [];
          setDepots(nextDepots);

          const defaultDepot = nextDepots.find(
            (depot) => depot.isDefault && isOperationalDepot(depot),
          );

          setForm((current) => ({
            ...current,
            depotId: current.depotId || (defaultDepot ? String(defaultDepot.id) : ""),
          }));

          setCoDrivers(coDriverPayload.coDrivers || []);
          setVehicles(vehiclePayload.vehicles || []);

          const nextFoRoutes = foRoutePayload.foRoutes || [];
          setFoRoutes(nextFoRoutes);

          setFleetOrders(fleetPayload.fleetOrders || []);
          setFoNumber(numberPayload.foNumber);

          const originalByKey = new Map(
            documents.map((order) => [
              order.documentKey ||
                `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`,
              order,
            ]),
          );

          setPrepared({
            ...preparedPayload,
            orders: (preparedPayload.orders || []).map((order) => {
              const key =
                order.documentKey ||
                `${order.documentType || "SALES_ORDER"}:${order.documentId || order.id}`;

              return {
                ...originalByKey.get(key),
                ...order,
              };
            }),
          });

          const inferred = nextFoRoutes.find(
            (route) =>
              route.active &&
              inferredRouteName &&
              route.foRouteName.toLowerCase() ===
                inferredRouteName.toLowerCase(),
          );

          if (inferred) {
            setForm((current) => ({
              ...current,
              foRouteId: String(inferred.id),
            }));
          }
        },
      )
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading || !form.foDate) return;

    getNextFleetOrderNumber(form.foDate)
      .then((payload) => setFoNumber(payload.foNumber))
      .catch((requestError) => setError(requestError.message));
  }, [form.foDate, loading]);

  async function selectFleetOrder(id) {
    setSelectedFleetOrderId(id);
    setSelectedFleetOrder(null);
    setError("");

    if (!id) return;

    try {
      const payload = await getFleetOrder(id);
      setSelectedFleetOrder(payload.fleetOrder);
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    if (!documentRefs.length) {
      setError("Pilih minimal satu Delivery Order.");
      setSaving(false);
      return;
    }

    try {
      const response =
        mode === "new"
          ? await createFleetOrder({ ...form, documents: documentRefs })
          : await addDocumentsToFleetOrder(
              selectedFleetOrderId,
              documentRefs,
            );

      onSuccess(response.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      className="dialog-card--wide"
      onClose={onCancel}
      title="Fleet Order"
    >
      <form className="fleet-order-modal" onSubmit={submit}>
        <div className="fleet-order-mode" role="tablist">
          <button
            aria-selected={mode === "new"}
            className={mode === "new" ? "is-active" : ""}
            onClick={() => {
              setMode("new");
              setError("");
            }}
            role="tab"
            type="button"
          >
            Buat Fleet Order Baru
          </button>

          <button
            aria-selected={mode === "existing"}
            className={mode === "existing" ? "is-active" : ""}
            onClick={() => {
              setMode("existing");
              setError("");
            }}
            role="tab"
            type="button"
          >
            Gabungkan ke Fleet Order Existing
          </button>
        </div>

        <div className="fleet-order-modal__body">
          {error && (
            <div className="error-banner" role="alert">
              {error}
            </div>
          )}

          {loading && (
            <div className="notice-banner">Mengambil detail pesanan...</div>
          )}

          {mode === "new" ? (
            <div className="fleet-order-form">
              <label>
                <span>Nomor FO</span>
                <input readOnly value={foNumber} />
              </label>

              <label>
                <span>Tanggal FO</span>
                <input
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      foDate: event.target.value,
                    }))
                  }
                  required
                  type="date"
                  value={form.foDate}
                />
              </label>

              <label>
                <span>Tanggal Berangkat</span>
                <input
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      departureDate: event.target.value,
                    }))
                  }
                  required
                  type="date"
                  value={form.departureDate}
                />
              </label>

              <label>
                <span>Estimasi Pulang</span>
                <input
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      estimatedReturnDate: event.target.value,
                    }))
                  }
                  required
                  type="date"
                  value={form.estimatedReturnDate}
                />
              </label>

              <label>
                <span>Depot / Gudang Asal</span>
                <select
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      depotId: event.target.value,
                    }))
                  }
                  required
                  value={form.depotId}
                >
                  <option value="">Pilih depot asal</option>

                  {depots
                    .filter((depot) => depot.active)
                    .map((depot) => (
                    <option
                      disabled={!isOperationalDepot(depot)}
                      key={depot.id}
                      value={depot.id}
                    >
                      {depotOptionLabel(depot)}
                    </option>
                  ))}
                </select>
                {!depots.some(isOperationalDepot) && (
                  <small className="field-help field-help--warning">
                    Belum ada depot aktif dengan koordinat valid. Periksa Data Master → Depot.
                  </small>
                )}
              </label>

              <label>
                <span>Nama Driver</span>
                <select
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      driverId: event.target.value,
                    }))
                  }
                  required
                  value={form.driverId}
                >
                  <option value="">Pilih driver</option>

                  {drivers
                    .filter((row) => row.active)
                    .map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.driverName}
                      </option>
                    ))}
                </select>
              </label>

              <label>
                <span>Nama Co-Driver</span>
                <select
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      coDriverId: event.target.value,
                    }))
                  }
                  value={form.coDriverId}
                >
                  <option value="">Tanpa Co-driver</option>

                  {coDrivers
                    .filter((row) => row.active)
                    .map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.coDriverName}
                      </option>
                    ))}
                </select>
              </label>

              <label>
                <span>Nopol Kendaraan</span>
                <select
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      vehicleId: event.target.value,
                    }))
                  }
                  required
                  value={form.vehicleId}
                >
                  <option value="">Pilih kendaraan</option>

                  {vehicles
                    .filter((row) => row.active)
                    .map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.vehiclePlate} · {row.vehicleType} ·{" "}
                        {row.capacityKg.toLocaleString("id-ID")} kg ·{" "}
                        {row.volumeM3.toLocaleString("id-ID")} m³
                      </option>
                    ))}
                </select>
              </label>

              <label>
                <span>Rute FO</span>
                <select
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      foRouteId: event.target.value,
                    }))
                  }
                  required
                  value={form.foRouteId}
                >
                  <option value="">Pilih Rute FO</option>

                  {foRoutes
                    .filter((route) => route.active)
                    .map((route) => (
                      <option key={route.id} value={route.id}>
                        {route.foRouteName}
                      </option>
                    ))}
                </select>
              </label>
            </div>
          ) : (
            <>
              <label className="fleet-order-existing-select">
                <span>Pilih Fleet Order</span>

                <select
                  onChange={(event) => selectFleetOrder(event.target.value)}
                  required
                  value={selectedFleetOrderId}
                >
                  <option value="">Pilih Fleet Order aktif</option>

                  {fleetOrders.map((fleetOrder) => (
                    <option key={fleetOrder.id} value={fleetOrder.id}>
                      {fleetOrder.foNumber} ·{" "}
                      {fleetOrder.foRouteName || fleetOrder.routeName} ·{" "}
                      {fleetOrder.depot?.depotName || "Depot belum dipilih"} ·{" "}
                      {fleetOrder.driverName}
                    </option>
                  ))}
                </select>
              </label>

              <ReadOnlyFleetOrder fleetOrder={selectedFleetOrder} />
            </>
          )}

          {prepared && (
            <>
              <div className="logistics-summary">
                <div>
                  <span>Total Dokumen</span>
                  <strong>{prepared.summary.salesOrderCount}</strong>
                </div>

                <div>
                  <span>Total Pelanggan</span>
                  <strong>{prepared.summary.customerCount}</strong>
                </div>

                <div>
                  <span>Total Berat</span>
                  <strong>
                    {formatWeight(prepared.summary.totalWeightKg)}
                  </strong>
                </div>

                <div>
                  <span>Total Volume</span>
                  <strong>
                    {formatVolume(prepared.summary.totalVolumeM3)}
                  </strong>
                </div>

                <div>
                  <span>Status Data Logistics</span>
                  <strong>
                    <LogisticsStatus order={prepared.summary} />
                  </strong>
                </div>
              </div>

              {prepared.summary.warningCount > 0 && (
                <div className="logistics-warning" role="status">
                  <strong>Data logistics belum lengkap.</strong>

                  <span>
                    {prepared.summary.warningCount} peringatan ditemukan. Fleet
                    Order tetap dapat dijadwalkan.
                  </span>

                  <ul>
                    {prepared.summary.warnings
                      .slice(0, 5)
                      .map((warning, index) => (
                        <li key={`${warning}-${index}`}>{warning}</li>
                      ))}
                  </ul>
                </div>
              )}

              <SelectedOrdersTable
                orders={prepared.orders}
                selectedKeys={selectedDocumentKeys}
                onToggle={(key) => setSelectedDocumentKeys((current) => {
                  const next = new Set(current);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                })}
              />
            </>
          )}
        </div>

        <footer className="dialog-card__actions">
          <button
            className="secondary-button"
            disabled={saving}
            onClick={onCancel}
            type="button"
          >
            Batalkan
          </button>

          <button
            className="primary-button"
            disabled={
              saving ||
              loading ||
              !prepared ||
              (mode === "existing" && !selectedFleetOrderId)
            }
            type="submit"
          >
            {saving ? "Menjadwalkan…" : "Jadwalkan"}
          </button>
        </footer>
      </form>
    </Dialog>
  );
}
